import { openedModelToStatePatch } from 'Pods/ThreeDStudio/Services/openedModelToStatePatch/openedModelToStatePatch'
import { ACTION, DEFAULT_MODEL_SETTINGS } from 'Pods/ThreeDStudio/Store/constants'

const EMPTY_SETTINGS = {}

let lastOpenedModel
let lastSettings = EMPTY_SETTINGS

/**
 * The fields an enhancement run overwrites on top of the settings inherited
 * from its source generation. Retexture replaces the material; remesh replaces
 * the polycount/polygon type/low poly; segmentation changes none of the fields
 * the details card shows.
 * @param {string} enhancedAction - patch.schema.enhancedAction
 * @param {Object} patch - openedModelToStatePatch result for the enhancement run
 * @returns {Object} - Partial settings to overlay on the inherited ones
 */
const enhancementOverrides = (enhancedAction, patch) => {
  if (enhancedAction === ACTION.RETEXTURE) {
    return { material: patch.textureSettings?.material }
  }

  if (enhancedAction === ACTION.MESH_SIMPLIFICATION) {
    const { targetPolycount, polygonType, isSmartLowPoly } = patch.remeshSettings || {}
    return { targetPolycount, polygonType, isSmartLowPoly }
  }

  return EMPTY_SETTINGS
}

const deriveSettings = (openedModel) => {
  const patch = openedModelToStatePatch(openedModel)
  const isEmpty = !patch.openedModel || Object.keys(patch.openedModel).length === 0

  if (isEmpty) {
    return EMPTY_SETTINGS
  }

  const enhancedAction = patch.schema?.enhancedAction

  if (!enhancedAction) {
    const { material, targetPolycount, polygonType, isSmartLowPoly, extraQuality } = {
      ...DEFAULT_MODEL_SETTINGS,
      ...(patch.modelSettings || {}),
    }

    return {
      generationProvider: patch.schema?.generationProvider,
      material,
      targetPolycount,
      polygonType,
      isSmartLowPoly,
      extraQuality,
    }
  }

  // Enhancement runs inherit the settings of the source generation that
  // produced the mesh and overwrite only what the enhancement changed. The
  // provider is the enhancement's own: it produced the displayed result.
  // Without a linked sourceRun there is nothing to inherit, so non-overridden
  // fields stay undefined and the card hides their rows.
  const sourcePatch = openedModel?.sourceRun ? openedModelToStatePatch(openedModel.sourceRun) : null
  const inherited = sourcePatch?.modelSettings
    ? { ...DEFAULT_MODEL_SETTINGS, ...sourcePatch.modelSettings }
    : EMPTY_SETTINGS

  const { material, targetPolycount, polygonType, isSmartLowPoly, extraQuality } = {
    ...inherited,
    ...enhancementOverrides(enhancedAction, patch),
  }

  return {
    generationProvider: patch.schema?.enhancedProvider,
    material,
    targetPolycount,
    polygonType,
    isSmartLowPoly,
    extraQuality,
  }
}

/**
 * Selects the generation settings that produced the opened model (library run)
 * by re-running openedModelToStatePatch on ui.openedModel, mirroring how
 * setOpenedModel seeds the modelSettings / schema slices. Deriving from the
 * immutable openedModel instead of reading those live slices keeps consumers
 * (e.g. the viewport ModelDetailsCard) fixed on the settings the displayed
 * model was generated with while the user edits the generation panel.
 *
 * Enhancement runs (retexture, remesh, segmentation) resolve against their
 * source generation (openedModel.sourceRun): the source settings are inherited
 * and only the fields the enhancement changed are overwritten.
 *
 * Memoized on the openedModel reference so the selector returns a stable
 * object between renders, as zustand requires.
 *
 * @param {Object} state - ThreeDStudio store state
 * @returns {Object} - { generationProvider, material, targetPolycount,
 * polygonType, isSmartLowPoly, extraQuality }, or {} when no parseable model is
 * open
 */
export const selectOpenedModelGenerationSettings = (state) => {
  const openedModel = state.ui?.openedModel

  if (openedModel !== lastOpenedModel) {
    lastOpenedModel = openedModel
    lastSettings = deriveSettings(openedModel)
  }

  return lastSettings
}
